import { useMemo } from 'react';
import { Box, Flex, Text, Grid } from '@chakra-ui/react';
import { EventCard } from '../EventCard/EventCard';
import type { CurrentActivity } from '@/types/events';
import type { Organization } from '@/types/events';
import { isEventLive } from '@/utils/isEventLive';

interface CurrentActivitiesSectionProps {
  activities: CurrentActivity[];
  onEventPress: (event: Organization) => void;
  /** Omitted for surfaces where leaving is not offered. */
  onLeave?: (activity: CurrentActivity) => void;
  /** Opens the event scrolled to its shifts. */
  onShifts?: (event: Organization) => void;
}

function activityToOrg(a: CurrentActivity): Organization {
  return {
    id: a.id,
    name: a.name,
    location: a.location,
    image: a.image ?? null,
    status: a.is_active ? 'Active' : 'Ended',
    start_date: a.start_date ?? null,
    end_date: a.end_date ?? null,
    slots: 0,
    volunteerCount: 0,
  };
}

type OrgGroup = {
  organization_id: string;
  organization_name: string;
  activities: CurrentActivity[];
};

export function CurrentActivitiesSection({
  activities,
  onEventPress,
  onLeave,
  onShifts,
}: CurrentActivitiesSectionProps) {
  const groups = useMemo<OrgGroup[]>(() => {
    const map = new Map<string, OrgGroup>();
    for (const a of activities) {
      const orgId = a.organization_id ?? 'unknown';
      const orgName = a.organization_name ?? 'Unknown Organization';
      if (!map.has(orgId)) {
        map.set(orgId, { organization_id: orgId, organization_name: orgName, activities: [] });
      }
      map.get(orgId)!.activities.push(a);
    }
    return [...map.values()];
  }, [activities]);

  if (activities.length === 0) return null;

  return (
    <Box as="section" my="5" animation="fadeIn 0.3s ease-in-out">
      <Text
        as="h2"
        m="0"
        mb="5"
        fontSize={{ base: 'lg', md: 'xl' }}
        fontWeight="bold"
        color="volyx.text"
        fontFamily="body"
        letterSpacing="wide"
      >
        Currently involved in...
      </Text>

      <Flex direction="column" gap="6">
        {groups.map((group) => (
          <Box key={group.organization_id}>
            {/* Org label — plain, no toggle */}
            <Flex align="center" gap="2" mb="3">
              <Box w="3px" h="18px" bg="volyx.primary" borderRadius="full" flexShrink={0} />
              <Text
                as="span"
                fontSize="sm"
                fontWeight="semibold"
                color="volyx.text"
                letterSpacing="wide"
                textTransform="uppercase"
              >
                {group.organization_name}
              </Text>
            </Flex>

            <Grid
              templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
              gap="4"
            >
              {group.activities.map((activity) => (
                <EventCard
                  key={activity.id}
                  event={activityToOrg(activity)}
                  variant="involved"
                  onPress={() => onEventPress(activityToOrg(activity))}
                  daysLeft={activity.daysLeft}
                  daysUntilStart={activity.daysUntilStart}
                  isEventStarted={activity.isEventStarted}
                  // An ended event is history: it can be viewed but not left.
                  onLeave={onLeave && isEventLive(activity) ? () => onLeave(activity) : undefined}
                  // Like Leave, only while the event runs -- after that its shifts are history.
                  onShifts={onShifts && isEventLive(activity) ? () => onShifts(activityToOrg(activity)) : undefined}
                />
              ))}
            </Grid>
          </Box>
        ))}
      </Flex>
    </Box>
  );
}
